import { useEffect, useState } from 'react';
import { Box, Image as ThemeUIImage } from 'theme-ui';

import { PENDING_UPLOAD } from '@/components/Collection/utils';
import { useGetSignedUploadImageByKeyQuery } from '@/graphql/types/generated';

/**
 * A read-only strip of one banner image, resolved the same way ImageUpload
 * resolves its preview: a picked-but-unsaved file shows from memory, a stored
 * key through a signed URL. Sized to the desktop banner's own aspect so a
 * list of them reads as banners rather than as a grid of squares.
 */
export default function BannerThumbnail({
  imageKey,
  pendingFile,
  width = 200,
  alt,
}: {
  imageKey: string;
  pendingFile?: File | null;
  width?: number;
  alt: string;
}) {
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const stored = imageKey && imageKey !== PENDING_UPLOAD ? imageKey : '';

  const [{ data }] = useGetSignedUploadImageByKeyQuery({
    variables: { key: stored },
    requestPolicy: 'cache-first',
    pause: !stored || !!pendingFile,
  });

  useEffect(() => {
    if (!pendingFile) {
      setLocalUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setLocalUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const url = pendingFile ? localUrl : data?.signedUploadImageByKey?.url;

  return (
    <Box
      sx={{
        width,
        aspectRatio: '2288 / 480',
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        bg: 'neutralLight',
      }}
    >
      {url && (
        <ThemeUIImage
          src={url}
          alt={alt}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
    </Box>
  );
}
